import { Alert, Button, Group, NumberInput, Select, SimpleGrid, Stack, Text, Textarea } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconArrowLeft, IconCalendarEvent, IconPlus } from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { NapakaApi, sporocilo } from '../api/napaka';
import { api, podatki, type Shema } from '../api/odjemalec';
import { datumZaApi } from '../oblika/oblika';
import { Kartica } from '../postavitev/Podatki';
import { GlavaZaslona, ZaslonSeznama } from '../seznam/Seznam';
import { obrezano, type VoziloStranke } from './delavnica';
import { IzbiraVozila } from './IzbiraVozila';
import { imeDne, ura, type Termin } from './termini';
import { StrankaNaloga } from './StrankaNaloga';
import { ustvariStranko, vpisanaNova, type NovaStranka } from './stranka';
import { PoljaVozila } from './PoljaVozila';

type Oseba = Shema['OsebaPodatki'];

/**
 * Sprejem vozila v delavnico (nov delovni nalog): vozilo poiščemo po registrski ali vpišemo novo, stranka je
 * lastnik vozila ali druga (tudi nova z imenom in telefonom – ustvari se ob sprejemu), nato kilometri, težava po besedah stranke, mehanik in
 * predviden zaključek. »Sprejmi vozilo« ustvari nalog in ga odpre. {@code ?vozilo=<id>} vozilo izbere vnaprej,
 * {@code ?termin=<id>} izpolni vozilo, stranko, težavo in mehanika s termina iz koledarja (termin dobi nalog).
 */
export function SprejemVozila() {
  const navigate = useNavigate();
  const odjemalec = useQueryClient();
  const [parametri] = useSearchParams();
  const zacetnoVozilo = Number(parametri.get('vozilo')) || null;
  const terminId = Number(parametri.get('termin')) || null;
  const [termin, setTermin] = useState<Termin | null>(null);
  const [vozilo, setVozilo] = useState<VoziloStranke | null>(null);
  const [novo, setNovo] = useState<VoziloStranke | null>(null);
  const [kupec, setKupec] = useState<Oseba | null>(null);
  const [nova, setNova] = useState<NovaStranka>(null);
  const [km, setKm] = useState<number | string>('');
  const [tezava, setTezava] = useState('');
  const [opomba, setOpomba] = useState('');
  const [mehanik, setMehanik] = useState<string | null>(null);
  const [predviden, setPredviden] = useState<string | null>(null);
  const [polja, setPolja] = useState<Record<string, string>>({});
  const [napaka, setNapaka] = useState<string | null>(null);
  const [shranjujem, setShranjujem] = useState(false);

  const moznosti = useQuery({
    queryKey: ['delavnica-moznosti'],
    queryFn: ({ signal }) => podatki(api.GET('/api/delavnica/nalogi/moznosti', { signal })),
  });

  const naloziStranko = async (id: number) => {
    try {
      setKupec(await podatki(api.GET('/api/osebe/{vrsta}/{id}', { params: { path: { vrsta: 'kupci', id } } })));
    } catch {
      // stranko lahko izbere sam
    }
  };

  // izbrano vozilo: lastnik postane stranka (če ta še ni izbrana)
  const izberiVozilo = async (v: VoziloStranke | null) => {
    setVozilo(v);
    if (v?.kupecId && !kupec) {
      await naloziStranko(v.kupecId);
    }
  };

  // sprejem s termina: vozilo, stranka, težava in mehanik s termina
  useEffect(() => {
    if (!terminId) {
      return;
    }
    void podatki(api.GET('/api/delavnica/termini/{id}', { params: { path: { id: terminId } } }))
      .then((t) => {
        setTermin(t);
        setVozilo(t.vozilo ?? null);
        setTezava(t.opis ?? '');
        setMehanik(t.mehanikId ? String(t.mehanikId) : null);
        return t.kupecId ? naloziStranko(t.kupecId) : undefined;
      })
      .catch((e: unknown) => setNapaka(sporocilo(e)));
  }, [terminId]);

  useEffect(() => {
    if (!zacetnoVozilo) {
      return;
    }
    void podatki(api.GET('/api/delavnica/vozila/{id}', { params: { path: { id: zacetnoVozilo } } }))
      .then((p) => izberiVozilo(p.vozilo ?? null))
      .catch((e: unknown) => setNapaka(sporocilo(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- samo ob odprtju zaslona z izbranim vozilom
  }, [zacetnoVozilo]);

  const sprejmi = async () => {
    setNapaka(null);
    setPolja({});
    setShranjujem(true);
    try {
      // vpisana nova stranka se ustvari ob sprejemu (tudi brez klika »Dodaj«)
      let stranka = kupec;
      if (!stranka && nova && vpisanaNova(nova)) {
        try {
          stranka = await ustvariStranko(nova);
        } catch (e) {
          if (e instanceof NapakaApi && e.polja.ime) {
            setPolja({ 'stranka.ime': e.polja.ime });
            return;
          }
          throw e;
        }
        setKupec(stranka);
        setNova(null);
      }
      const nalog = await podatki(
        api.POST('/api/delavnica/nalogi', {
          body: {
            voziloId: novo ? undefined : (vozilo?.id ?? undefined),
            vozilo: novo ?? undefined,
            kupecId: stranka?.id ?? undefined,
            km: typeof km === 'number' ? km : undefined,
            tezava: obrezano(tezava),
            opomba: obrezano(opomba),
            mehanikId: mehanik ? Number(mehanik) : undefined,
            predvidenZakljucek: predviden ?? undefined,
            postavke: [],
            terminId: termin?.id ?? undefined,
          },
        }),
      );
      await odjemalec.invalidateQueries({ queryKey: ['delovni-nalogi'] });
      await odjemalec.invalidateQueries({ queryKey: ['vozila-strank'] });
      await odjemalec.invalidateQueries({ queryKey: ['delavnica-pregled'] });
      await odjemalec.invalidateQueries({ queryKey: ['termini'] });
      notifications.show({ color: 'green', message: `Vozilo je sprejeto, nalog ${nalog.stevilka}.` });
      void navigate(`/delavnica/nalogi/${nalog.id}`, { replace: true });
    } catch (e) {
      if (e instanceof NapakaApi && Object.keys(e.polja).length > 0) {
        setPolja(e.polja);
      } else {
        setNapaka(sporocilo(e));
      }
    } finally {
      setShranjujem(false);
    }
  };

  return (
    <ZaslonSeznama>
      <Button
        component={Link}
        to="/delavnica/nalogi"
        variant="subtle"
        leftSection={<IconArrowLeft size={16} />}
        mb="xs"
        px={0}
      >
        Delovni nalogi
      </Button>
      <GlavaZaslona naslov="Sprejem vozila" opis="Nov delovni nalog." pomoc="delovni-nalogi" />
      {termin && (
        <Alert color="blue" variant="light" icon={<IconCalendarEvent size={18} />} mb="md">
          Termin {termin.zacetek ? `${imeDne(termin.zacetek)} ob ${ura(termin.zacetek)}` : ''}
          {termin.kupec ? ` · ${termin.kupec}` : ''} – ob sprejemu se termin poveže z nalogom.
        </Alert>
      )}
      {polja.terminId && (
        <Alert color="red" variant="light" icon={<IconAlertCircle size={18} />} mb="md" role="alert">
          {polja.terminId}
        </Alert>
      )}
      {napaka && (
        <Alert color="red" variant="light" icon={<IconAlertCircle size={18} />} mb="md" role="alert">
          {napaka}
        </Alert>
      )}
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
        <Stack gap="md">
          <Kartica naslov="Vozilo">
            {novo ? (
              <Stack gap="sm">
                <PoljaVozila vozilo={novo} nastavi={setNovo} polja={polja} predpona="vozilo." />
                <Group>
                  <Button variant="subtle" onClick={() => setNovo(null)}>
                    Poišči vpisano vozilo
                  </Button>
                </Group>
              </Stack>
            ) : (
              <Stack gap="sm">
                <IzbiraVozila izbrano={vozilo} izberi={(v) => void izberiVozilo(v)} napaka={polja.voziloId} />
                {vozilo && (
                  <Text size="sm" c="dimmed">
                    {[vozilo.vin, vozilo.motor, vozilo.gorivo, vozilo.kupec].filter(Boolean).join(' · ')}
                  </Text>
                )}
                <Group>
                  <Button
                    variant="light"
                    leftSection={<IconPlus size={18} />}
                    onClick={() => {
                      setVozilo(null);
                      setNovo({});
                    }}
                  >
                    Novo vozilo
                  </Button>
                </Group>
              </Stack>
            )}
          </Kartica>
          <Kartica naslov="Stranka">
            <StrankaNaloga kupec={kupec} izberi={setKupec} nova={nova} nastaviNovo={setNova} polja={polja} />
          </Kartica>
        </Stack>
        <Kartica naslov="Sprejem">
          <Stack gap="sm">
            <NumberInput
              label="Kilometri ob sprejemu"
              value={km}
              onChange={setKm}
              error={polja.km}
              allowDecimal={false}
              allowNegative={false}
              thousandSeparator="."
              decimalSeparator=","
            />
            <Textarea
              label="Težava po besedah stranke"
              value={tezava}
              onChange={(e) => setTezava(e.currentTarget.value)}
              onBlur={() => setTezava((t) => t.trim())}
              error={polja.tezava}
              autosize
              minRows={3}
            />
            <SimpleGrid cols={{ base: 1, xs: 2 }} spacing="sm">
              <Select
                label="Mehanik"
                data={(moznosti.data?.mehaniki ?? []).map((m) => ({ value: String(m.id), label: m.ime ?? '' }))}
                value={mehanik}
                onChange={setMehanik}
                error={polja.mehanikId}
                clearable
              />
              <DatePickerInput
                label="Predviden zaključek"
                value={predviden}
                onChange={(d) => setPredviden(datumZaApi(d) ?? null)}
                valueFormat="D. M. YYYY"
                clearable
              />
            </SimpleGrid>
            <Textarea
              label="Interna opomba"
              description="Stranka je ne vidi."
              value={opomba}
              onChange={(e) => setOpomba(e.currentTarget.value)}
              onBlur={() => setOpomba((t) => t.trim())}
              error={polja.opomba}
              autosize
              minRows={2}
            />
          </Stack>
        </Kartica>
      </SimpleGrid>
      <Group justify="flex-end" mt="md">
        <Button size="md" onClick={() => void sprejmi()} loading={shranjujem}>
          Sprejmi vozilo
        </Button>
      </Group>
    </ZaslonSeznama>
  );
}
